import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLessonPlanService } from "../apis/lessonPlan/useLessonPlanService";
import {
  TCreateInventoryLinkRequest,
  TCreateLessonPlanRequest,
  TCreateSessionRequest,
  TCreateUnitRequest,
  TInventoryLinkDeleteAck,
  TInventoryLinkMutationResponse,
  TLessonPlan,
  TLessonPlanListFilters,
  TLessonPlanListResponse,
  TLessonPlanMutationAck,
  TSessionDeleteAck,
  TSessionMutationResponse,
  TUnitDeleteAck,
  TUnitMutationResponse,
  TUpdateInventoryLinkRequest,
  TUpdateLessonPlanRequest,
  TUpdateSessionRequest,
  TUpdateUnitRequest,
} from "../apis/lessonPlan/lessonPlan.interfaces";
import { useInstituteContext } from "../context/institute.context";

const lessonPlanListKey = (instituteId?: string) => [
  "lesson-plans",
  instituteId,
];

const lessonPlanByIdKey = (id: string) => ["lesson-plan", id];

export const useLessonPlanListHook = (
  filters?: Omit<TLessonPlanListFilters, "instituteId">,
) => {
  const { getLessonPlans } = useLessonPlanService();
  const { selectedInstitute } = useInstituteContext();

  const instituteId = selectedInstitute?.id;
  const mergedFilters: TLessonPlanListFilters = {
    ...filters,
    instituteId,
  };

  return useQuery<TLessonPlanListResponse>({
    queryKey: ["lesson-plans", instituteId, mergedFilters],
    queryFn: () => getLessonPlans(mergedFilters),
    enabled: !!instituteId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useLessonPlanByIdHook = (id?: string) => {
  const { getLessonPlanById } = useLessonPlanService();

  return useQuery<TLessonPlan>({
    queryKey: ["lesson-plan", id],
    queryFn: () => getLessonPlanById(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateLessonPlanHook = () => {
  const { createLessonPlan } = useLessonPlanService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TLessonPlan, Error, TCreateLessonPlanRequest>({
    mutationFn: (request) => createLessonPlan(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: lessonPlanListKey(selectedInstitute?.id),
      });
    },
  });
};

export const useUpdateLessonPlanHook = () => {
  const { updateLessonPlan } = useLessonPlanService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TLessonPlan,
    Error,
    { id: string; request: TUpdateLessonPlanRequest }
  >({
    mutationFn: ({ id, request }) => updateLessonPlan(id, request),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({
        queryKey: lessonPlanListKey(selectedInstitute?.id),
      });
      queryClient.invalidateQueries({ queryKey: lessonPlanByIdKey(updated.id) });
    },
  });
};

export const useDeleteLessonPlanHook = () => {
  const { deleteLessonPlan } = useLessonPlanService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TLessonPlanMutationAck, Error, { id: string }>({
    mutationFn: ({ id }) => deleteLessonPlan(id),
    onSuccess: (_ack, vars) => {
      queryClient.invalidateQueries({
        queryKey: lessonPlanListKey(selectedInstitute?.id),
      });
      queryClient.removeQueries({ queryKey: lessonPlanByIdKey(vars.id) });
    },
  });
};

export const useArchiveLessonPlanHook = () => {
  const { archiveLessonPlan } = useLessonPlanService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<TLessonPlan, Error, { id: string }>({
    mutationFn: ({ id }) => archiveLessonPlan(id),
    onSuccess: (archived) => {
      queryClient.invalidateQueries({
        queryKey: lessonPlanListKey(selectedInstitute?.id),
      });
      queryClient.invalidateQueries({ queryKey: lessonPlanByIdKey(archived.id) });
    },
  });
};

// ---------------------------------------------------------------------------
// Child mutations — every response carries lessonPlan.{id, version, updatedAt}
// (BE bumps both inside the same txn). We invalidate the cached lesson-plan
// query so the next read sees the bumped version.
// ---------------------------------------------------------------------------

const useChildInvalidator = () => {
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();
  return (lessonPlanId: string) => {
    queryClient.invalidateQueries({ queryKey: lessonPlanByIdKey(lessonPlanId) });
    queryClient.invalidateQueries({
      queryKey: lessonPlanListKey(selectedInstitute?.id),
    });
  };
};

export const useCreateUnitHook = () => {
  const { createUnit } = useLessonPlanService();
  const invalidate = useChildInvalidator();

  return useMutation<
    TUnitMutationResponse,
    Error,
    { lessonPlanId: string; request: TCreateUnitRequest }
  >({
    mutationFn: ({ lessonPlanId, request }) => createUnit(lessonPlanId, request),
    onSuccess: (_, vars) => invalidate(vars.lessonPlanId),
  });
};

export const useUpdateUnitHook = () => {
  const { updateUnit } = useLessonPlanService();
  const invalidate = useChildInvalidator();

  return useMutation<
    TUnitMutationResponse,
    Error,
    {
      lessonPlanId: string;
      unitId: string;
      request: TUpdateUnitRequest;
    }
  >({
    mutationFn: ({ lessonPlanId, unitId, request }) =>
      updateUnit(lessonPlanId, unitId, request),
    onSuccess: (_, vars) => invalidate(vars.lessonPlanId),
  });
};

export const useDeleteUnitHook = () => {
  const { deleteUnit } = useLessonPlanService();
  const invalidate = useChildInvalidator();

  return useMutation<
    TUnitDeleteAck,
    Error,
    { lessonPlanId: string; unitId: string }
  >({
    mutationFn: ({ lessonPlanId, unitId }) => deleteUnit(lessonPlanId, unitId),
    onSuccess: (_, vars) => invalidate(vars.lessonPlanId),
  });
};

export const useCreateSessionHook = () => {
  const { createSession } = useLessonPlanService();
  const invalidate = useChildInvalidator();

  return useMutation<
    TSessionMutationResponse,
    Error,
    {
      lessonPlanId: string;
      unitId: string;
      request: TCreateSessionRequest;
    }
  >({
    mutationFn: ({ lessonPlanId, unitId, request }) =>
      createSession(lessonPlanId, unitId, request),
    onSuccess: (_, vars) => invalidate(vars.lessonPlanId),
  });
};

export const useUpdateSessionHook = () => {
  const { updateSession } = useLessonPlanService();
  const invalidate = useChildInvalidator();

  return useMutation<
    TSessionMutationResponse,
    Error,
    {
      lessonPlanId: string;
      unitId: string;
      sessionId: string;
      request: TUpdateSessionRequest;
    }
  >({
    mutationFn: ({ lessonPlanId, unitId, sessionId, request }) =>
      updateSession(lessonPlanId, unitId, sessionId, request),
    onSuccess: (_, vars) => invalidate(vars.lessonPlanId),
  });
};

export const useDeleteSessionHook = () => {
  const { deleteSession } = useLessonPlanService();
  const invalidate = useChildInvalidator();

  return useMutation<
    TSessionDeleteAck,
    Error,
    { lessonPlanId: string; unitId: string; sessionId: string }
  >({
    mutationFn: ({ lessonPlanId, unitId, sessionId }) =>
      deleteSession(lessonPlanId, unitId, sessionId),
    onSuccess: (_, vars) => invalidate(vars.lessonPlanId),
  });
};

export const useCreateInventoryLinkHook = () => {
  const { createInventoryLink } = useLessonPlanService();
  const invalidate = useChildInvalidator();

  return useMutation<
    TInventoryLinkMutationResponse,
    Error,
    { lessonPlanId: string; request: TCreateInventoryLinkRequest }
  >({
    mutationFn: ({ lessonPlanId, request }) =>
      createInventoryLink(lessonPlanId, request),
    onSuccess: (_, vars) => invalidate(vars.lessonPlanId),
  });
};

export const useUpdateInventoryLinkHook = () => {
  const { updateInventoryLink } = useLessonPlanService();
  const invalidate = useChildInvalidator();

  return useMutation<
    TInventoryLinkMutationResponse,
    Error,
    {
      lessonPlanId: string;
      inventoryLinkId: string;
      request: TUpdateInventoryLinkRequest;
    }
  >({
    mutationFn: ({ lessonPlanId, inventoryLinkId, request }) =>
      updateInventoryLink(lessonPlanId, inventoryLinkId, request),
    onSuccess: (_, vars) => invalidate(vars.lessonPlanId),
  });
};

export const useDeleteInventoryLinkHook = () => {
  const { deleteInventoryLink } = useLessonPlanService();
  const invalidate = useChildInvalidator();

  return useMutation<
    TInventoryLinkDeleteAck,
    Error,
    { lessonPlanId: string; inventoryLinkId: string }
  >({
    mutationFn: ({ lessonPlanId, inventoryLinkId }) =>
      deleteInventoryLink(lessonPlanId, inventoryLinkId),
    onSuccess: (_, vars) => invalidate(vars.lessonPlanId),
  });
};
